<!--
     <InlineViewer
       :unit="QUnit"
       @update="update_qunit(new_qunit)"
     />

     Public methods:

     - export_state() → Returns an object representing the UX state
     - import_state(state) → Imports an state exported by export_state()
-->

<template>
  <div class="qmkey-explorer-table-expansion-cont">
    <div v-if="tabs">
      <v-tabs
        v-if="!is_mobile"
        v-model="tab"
        icons-and-text
        align-with-title
        background-color="#cac8c9"
        height = 32
        class="qmkey-explorer-table-expansion-panel"
        active-class="qmkey-explorer-table-expansion-panel-active"
      >
        <v-tabs-slider style="display: none"></v-tabs-slider>
        <v-tab
          v-for="(tab, i) in unit.inline_boxes"
          :key="i"
          class="qmkey-explorer-table-expansion-panel-header"
        >
          <div class="tab">
            {{ tab.name }}
          </div>
        </v-tab>
      </v-tabs>
      
      <div
        v-else
        class="qmkey-explorer-inline-chip-tabs"
      >
        <v-chip
          small
          v-for="(inline_tab, i) in unit.inline_boxes"
          :key="i"
          class="qmkey-explorer-tab-chip"
          @click="update_tab(i)"
          :color="i === tab ? 'primary' : 'white'"
        >
          <div class="tab">
            {{ inline_tab.name }}
          </div>
        </v-chip>
      </div>
    </div>

    <v-tabs-items
      v-model="tab"
      class="qmkey-explorer-table-expansion-panel-contents"
      touchless
    >
      <v-tab-item
        v-for="(tab, i) in unit.inline_boxes"
        :key="i"
        class="qmkey-explorer-table-expansion-panel-content"
      >
        <!-- QUnit data -->
        <InlineBoxData
          v-if="tab.type == 'inline-box-data'"
          :current_qunit="unit"
          :props="tab.props"
          :is_mobile="is_mobile"
          @download-evidence="handle_evidence"
        />

        <InlineBoxRelated
          v-else-if="tab.type == 'inline-box-related'"
          :qunits="tab.qunits"
          :qunit="unit"
          :props="tab.props"
          :pk="unit.uid"
          @update="update"
          @local_update="local_update"
          :is_mobile="is_mobile"
          :tab="tab"
        />

        <InlineBoxIndicator
          v-else-if="tab.type == 'inline-box-indicator'"
          :qunits="tab.qunits"
          :props="tab.props"
          :pk="pk"
          @update="update"
          :is_mobile="is_mobile"
        />

        <InlineBoxIndicatorTracking
          v-else-if="tab.type == 'inline-box-indicator-tracking'"
          :qunits="tab.qunits"
          :props="tab.props"
          :pk="pk"
          @update="update"
          :is_mobile="is_mobile"
        />

        <InlineBoxIndicatorRisk
          v-else-if="tab.type == 'inline-box-indicator-risk'"
          :qunits="tab.qunits"
          :props="tab.props"
          :pk="pk"
          @update="update"
          :is_mobile="is_mobile"
        />

        <InlineBoxComunications
          v-else-if="tab.type == 'inline-box-comunications'"
          :current_qunit="unit"
          :props="tab.props"
          :is_mobile="is_mobile"
        />

        <!-- WORKFLOW -->
        <InlineBoxWorkflow
          v-else-if="tab.type == 'inline-box-workflow'"
          :current_qunit="unit"
          :props="tab.props"
        />

        <!-- Versions -->
        <InlineBoxVersions
          v-else-if="tab.type == 'inline-box-versions'"
          :current_qunit="unit"
          @download-evidence="handle_evidence"
          :props="tab.props"
        />
        <!-- SimpleLayout -->
        <InlineBoxSimpleLayout
          v-else-if="tab.type == 'inline-box-simple-layout'"
          :current_qunit="unit"
          :props="tab.props"
        />

        <inline-box-download-tracker
          v-else-if="tab.type == 'inline-box-download-tracker'"
          :current_qunit="unit"
          :props="tab.props"
        ></inline-box-download-tracker>

        <InlineBoxRelatedFiles
          v-else-if="tab.type == 'inline-box-documents'"
          :current_qunit="unit"
          @download-evidence="handle_evidence"
          :props="tab.props"
        />
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>


<style>
.qmkey-explorer-tab-chip {
  margin: 0.5em;
}
.qmkey-explorer-inline-chip-tabs {
  background-color: primary;
}
</style>

<script>
 import { mapState } from 'vuex'
 import { downloadFile } from '@/utils/browserfiles';
 import QUnitAPI from '@/api/QUnit'

 import { QUnitField } from '@/qunit/QFields';
 import QUnit from '@/qunit/QUnit';
 import QUnitCollection from '@/qunit/QUnitCollection';
 import Field from '@/components/qfields/Field';

 import InlineBoxRelated from './InlineBoxRelated';
 import InlineBoxData from './InlineBoxData';
 import InlineBoxVersions from './InlineBoxVersions';
 import InlineBoxWorkflow from './InlineBoxWorkflow';
 import InlineBoxComunications from './InlineBoxComunications';
 import InlineBoxSimpleLayout from './InlineBoxSimpleLayout';
 import InlineBoxDownloadTracker from './InlineBoxVersionsDownloadTracker.vue';
 import InlineBoxIndicatorTracking from './InlineBoxIndicatorTracking.vue';
 import InlineBoxIndicator from './InlineBoxIndicator.vue';
 import InlineBoxIndicatorRisk from './InlineBoxIndicatorRisk.vue';
 import InlineBoxRelatedFiles from './InlineBoxRelatedFiles.vue';

 export default {
   name: 'InlineViewer',

   components: {
     Field,
     InlineBoxRelated,
     InlineBoxData,
     InlineBoxVersions,
     InlineBoxWorkflow,
     InlineBoxComunications,
     InlineBoxSimpleLayout,
     InlineBoxDownloadTracker,
     InlineBoxIndicatorTracking,
     InlineBoxIndicator,
     InlineBoxIndicatorRisk,
     InlineBoxRelatedFiles,
   },

   props: {
     unit: {required: true},
     size: {default: 'small'},
     tabs: {type: Boolean, default: true},
   },

   data: () => ({
     tab: 0,
     pk: null,
   }),

   computed: {
    ...mapState(['is_mobile']),
   },

   methods: {
     export_state() {
       // Returns current inline viewer state
       return {
         tab: this.tab,
         pk: this.pk, 
       };
     },

     import_state(state) {
       // Import an state exported by export_state()
       this.tab = state.tab || 0;
       this.pk = state.pk || null;
     },

     update(kid_data) {
      // At the end neew reload current QUnit so don't need 
      // update relations first
      this.pk = kid_data.uid;
       this.update_qunit_after();
     },

     local_update(unit) {
      if (unit) {
        this.$emit('update', unit);
      }
     },

     update_tab(new_tab){
      this.tab = new_tab;
     },

     async handle_evidence(current_row) {
       const row = current_row ? current_row : this.unit.current_state_document;
       const title = row.downloable_title || row.title
       
        QUnitAPI
          .getQUnitFile(this.unit._protected.centro, row.qunit_id, row.attribute, row.value, row.version, title, row.pmethod)  // TODO: This requires site_id API support
          .then((data) => {
            downloadFile(data, title);
            if (row.version) {
              this.update_qunit_after();
            }
          })
          .catch((error) => console.log('handle_evidence', error));
     
     },

     async update_qunit_after() {
      const updated_data = await QUnitAPI.getQUnit(this.unit._protected.centro, this.unit.uid);
      let new_qunit = new QUnit(updated_data);
      this.$emit('update', new_qunit);
     }
   },
 }
</script>
